import { useModal, useRouter } from '@application/utils';
import { DogBreederApplicationStatus } from '@gql/graphql';
import { MtApplicationsBreederRejectModal } from '@presentation/components';
import type { MtApplicationPageApplication } from '@presentation/pages';
import { MtApplicationPage } from '@presentation/pages';
import { FormikProvider } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { useApplicationMessageModal } from '../../ApplicationMessageModalController';
import { MtApplicationsOwnModalController } from '../../MtApplicationsOwnModalController';
import { useGetConversations } from '../MtMessagesPageController/api/useGetConversations';
import { useGetApplication } from './api/useGetApplication';
import { useOpenApplication } from './api/useOpenApplication';
import { useFormatApplication } from './useFormatApplication';
import { useSharedApplicationBreeder } from './useSharedApplicationBreeder';

export const MtApplicationBreederPageController = () => {
  const { infoFromCurrentPath, goBack, translate, navigateToRoute } = useRouter();
  const { setAccountId, show } = useApplicationMessageModal();
  const { conversations } = useGetConversations();
  const { openApplication } = useOpenApplication();

  const applicationId = Number(infoFromCurrentPath.params[0]);

  const { applicationLoading, application } = useGetApplication(applicationId);
  const { formatApplication } = useFormatApplication();

  const formattedApplication = useMemo((): MtApplicationPageApplication | null => {
    return formatApplication(application);
  }, [application, formatApplication]);

  const backClickHandler = useCallback(() => {
    goBack();
  }, [goBack]);

  const {
    deleteModalForm,
    deleteModalProps,
    showDeleteModal,
    shouldShowOwnAction,
    shouldShowRefuseAction,
    shouldShowMessagesAction,
  } = useSharedApplicationBreeder(applicationId);

  const { show: showOwnModal, modal: ownModal } = useModal();

  const deleteClickHandler = useCallback(() => {
    showDeleteModal();
  }, [showDeleteModal]);

  const ownClickHandler = useCallback(() => {
    showOwnModal();
  }, [showOwnModal]);

  const otherAccountId: number | undefined = useMemo(() => {
    const app = application?.accountMe?.dogBreeder?.applications?.[0];
    return app?.account?.id ?? application?.accountMe?.dogBreeder?.id;
  }, [application?.accountMe?.dogBreeder?.applications, application?.accountMe?.dogBreeder?.id]);

  const existingConversationId: number | undefined = useMemo(() => {
    return conversations.find((x) => x.otherAccountId === otherAccountId)?.id;
  }, [conversations, otherAccountId]);

  useEffect(() => {
    setAccountId(otherAccountId);
  }, [otherAccountId, setAccountId]);

  const messagesClickHandler = useCallback(() => {
    if (existingConversationId && otherAccountId) {
      navigateToRoute('mtMessages', { params: [otherAccountId.toString()] });
    } else {
      show();
    }
  }, [existingConversationId, navigateToRoute, otherAccountId, show]);

  const applicationStatus = useMemo(() => {
    return application?.accountMe?.dogBreeder?.applications?.[0]?.status;
  }, [application?.accountMe?.dogBreeder?.applications]);

  const status = useMemo(() => {
    if (
      [
        DogBreederApplicationStatus.AWAITING_PAYMENT,
        DogBreederApplicationStatus.CANCELED,
        DogBreederApplicationStatus.EXPIRED,
        DogBreederApplicationStatus.REFUNDED,
      ].includes(applicationStatus!)
    ) {
      return translate(`mt.breeder.applications.statusBar.${applicationStatus}`);
    }
  }, [applicationStatus, translate]);

  useEffect(() => {
    if (!application || application?.accountMe?.dogBreeder?.applications?.[0]?.seenByBreeder) {
      return;
    }

    openApplication(applicationId);
  }, [application, applicationId, openApplication]);

  return (
    <>
      <MtApplicationPage
        loading={applicationLoading}
        application={formattedApplication}
        personType="breeder"
        onBackClick={backClickHandler}
        onDeleteClick={shouldShowRefuseAction(applicationStatus) ? deleteClickHandler : undefined}
        onOwnClick={shouldShowOwnAction(applicationStatus) ? ownClickHandler : undefined}
        onMessagesClick={
          shouldShowMessagesAction(applicationStatus) ? messagesClickHandler : undefined
        }
        status={status}
      />
      <FormikProvider value={deleteModalForm}>
        <MtApplicationsBreederRejectModal {...deleteModalProps} />
      </FormikProvider>
      <MtApplicationsOwnModalController
        modal={ownModal}
        applicationId={applicationId}
        breeder={application?.accountMe?.dogBreeder}
        onRedirect={() => navigateToRoute('mtOwners')}
      />
    </>
  );
};
